import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import PortfolioContent from '../components/PortfolioContent';
import { getFxPalette, onThemeChange, currentTheme } from '../theme/themeColors';
import './Design6.css';

function Design6() {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const particlesRef = useRef(null);
    const linesRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });
    const rafRef = useRef(null);
    const timeRef = useRef(0);
    const colorTargetRef = useRef(null);
    const colorCurrentRef = useRef(null);
    const cursorGlowRef = useRef(null);

    useEffect(() => {
        // --- Cursor Glow ---
        const cursorGlow = document.createElement('div');
        cursorGlow.className = 'design6__cursor-glow';
        document.body.appendChild(cursorGlow);
        cursorGlowRef.current = cursorGlow;

        const handleMouseMove = (e) => {
            mouseRef.current.targetX = (e.clientX / window.innerWidth) * 2 - 1;
            mouseRef.current.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
            cursorGlow.style.left = e.clientX + 'px';
            cursorGlow.style.top = e.clientY + 'px';
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });

        // --- Theme palette (re-read when the theme changes) ---
        let fx = getFxPalette();
        const particleColor = new THREE.Color(fx.particle);
        colorTargetRef.current = new THREE.Color(fx.particle);
        colorCurrentRef.current = new THREE.Color(fx.particle);
        const buildSectionColors = () => [
            new THREE.Color(fx.particle),
            new THREE.Color(fx.particleAlt),
            new THREE.Color(fx.particle),
            new THREE.Color(fx.particleAlt),
            new THREE.Color(fx.particleAlt),
            new THREE.Color(fx.particle),
        ];
        let sectionColors = buildSectionColors();

        // --- Three.js Setup ---
        const mount = mountRef.current;
        const w = window.innerWidth;
        const h = window.innerHeight;

        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 1000);
        camera.position.z = 30;
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(w, h);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // --- Particle System (clustered) ---
        const PARTICLE_COUNT = 100;
        const positions = new Float32Array(PARTICLE_COUNT * 3);
        const colors = new Float32Array(PARTICLE_COUNT * 3);
        const sizes = new Float32Array(PARTICLE_COUNT);
        const velocities = new Float32Array(PARTICLE_COUNT * 3);
        const originalPositions = new Float32Array(PARTICLE_COUNT * 3);

        // Create 6 cluster centers spread across the view
        const clusters = [];
        for (let c = 0; c < 6; c++) {
            clusters.push({
                cx: (Math.random() - 0.5) * 40,
                cy: (Math.random() - 0.5) * 28,
                cz: (Math.random() - 0.5) * 20 - 3,
                spread: 3 + Math.random() * 8,
            });
        }

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const i3 = i * 3;
            // Pick a random cluster and distribute around it with Gaussian-like falloff
            const cluster = clusters[Math.floor(Math.random() * clusters.length)];
            const gaussRandom = () => {
                let u = 0, v = 0;
                while (u === 0) u = Math.random();
                while (v === 0) v = Math.random();
                return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
            };
            positions[i3] = cluster.cx + gaussRandom() * cluster.spread;
            positions[i3 + 1] = cluster.cy + gaussRandom() * cluster.spread;
            positions[i3 + 2] = cluster.cz + gaussRandom() * cluster.spread * 0.6;

            originalPositions[i3] = positions[i3];
            originalPositions[i3 + 1] = positions[i3 + 1];
            originalPositions[i3 + 2] = positions[i3 + 2];

            colors[i3] = particleColor.r;
            colors[i3 + 1] = particleColor.g;
            colors[i3 + 2] = particleColor.b;

            sizes[i] = Math.random() * 3.5 + 2.0;

            velocities[i3] = (Math.random() - 0.5) * 0.015;
            velocities[i3 + 1] = (Math.random() - 0.5) * 0.015;
            velocities[i3 + 2] = (Math.random() - 0.5) * 0.01;
        }

        const particleGeometry = new THREE.BufferGeometry();
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        const particleMaterial = new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uColor: { value: new THREE.Color(fx.particle) },
            },
            vertexShader: /* glsl */ `
                attribute float size;
                varying vec3 vColor;
                uniform float uTime;
                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    gl_PointSize = size * (200.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                    vColor = color;
                }
            `,
            fragmentShader: /* glsl */ `
                varying vec3 vColor;
                void main() {
                    float d = length(gl_PointCoord - 0.5) * 2.0;
                    float alpha = 1.0 - smoothstep(0.0, 1.0, d);
                    alpha *= 0.7;
                    float glow = exp(-d * 3.0) * 0.4;
                    alpha += glow;
                    gl_FragColor = vec4(vColor, alpha);
                }
            `,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            vertexColors: true,
        });

        const particles = new THREE.Points(particleGeometry, particleMaterial);
        scene.add(particles);
        particlesRef.current = particles;

        // --- Connection Lines ---
        const MAX_LINES = 250;
        const MAX_DIST = 9;

        const lineGeometry = new THREE.BufferGeometry();
        const linePositionsBuf = new Float32Array(MAX_LINES * 6);
        const lineColorsBuf = new Float32Array(MAX_LINES * 6);
        lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositionsBuf, 3));
        lineGeometry.setAttribute('color', new THREE.BufferAttribute(lineColorsBuf, 3));

        const lineMaterial = new THREE.LineBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.2,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });

        const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
        scene.add(lines);
        linesRef.current = lines;

        // Additive blending glows on dark; normal blending reads as ink on light
        const applyThemeBlending = () => {
            const dark = currentTheme() === 'dark';
            particleMaterial.blending = dark ? THREE.AdditiveBlending : THREE.NormalBlending;
            lineMaterial.blending = dark ? THREE.AdditiveBlending : THREE.NormalBlending;
            particleMaterial.needsUpdate = true;
            lineMaterial.needsUpdate = true;
        };
        applyThemeBlending();

        const particleData = { positions, colors, sizes, velocities, originalPositions, PARTICLE_COUNT, MAX_LINES, MAX_DIST, linePositionsBuf, lineColorsBuf, lineGeometry, lines };

        // --- Ambient Glow Rings ---
        const ringGeometry = new THREE.TorusGeometry(12, 0.03, 16, 100);
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0x00f0ff,
            transparent: true,
            opacity: 0.06,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
        });
        const ring1 = new THREE.Mesh(ringGeometry, ringMaterial);
        ring1.rotation.x = Math.PI * 0.4;
        ring1.rotation.y = Math.PI * 0.2;
        // scene.add(ring1);

        const ring2 = new THREE.Mesh(
            new THREE.TorusGeometry(16, 0.02, 16, 80),
            new THREE.MeshBasicMaterial({
                color: 0x7c3aed,
                transparent: true,
                opacity: 0.04,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
            })
        );
        ring2.rotation.x = Math.PI * 0.6;
        ring2.rotation.y = -Math.PI * 0.3;
        // scene.add(ring2);

        const ring3 = new THREE.Mesh(
            new THREE.TorusGeometry(20, 0.015, 16, 64),
            new THREE.MeshBasicMaterial({
                color: 0xf472b6,
                transparent: true,
                opacity: 0.03,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
            })
        );
        ring3.rotation.x = -Math.PI * 0.5;
        ring3.rotation.y = Math.PI * 0.15;
        // scene.add(ring3);

        // --- Animation Loop ---
        const animate = () => {
            const time = timeRef.current;
            timeRef.current += 0.005;

            mouseRef.current.x += (mouseRef.current.targetX - mouseRef.current.x) * 0.03;
            mouseRef.current.y += (mouseRef.current.targetY - mouseRef.current.y) * 0.03;

            colorCurrentRef.current.lerp(colorTargetRef.current, 0.02);

            camera.position.x = mouseRef.current.x * 3;
            camera.position.y = mouseRef.current.y * 2;
            camera.lookAt(0, 0, 0);

            ring1.rotation.z += 0.0003;
            ring1.rotation.x += 0.0002;
            ring2.rotation.z -= 0.0004;
            ring2.rotation.y += 0.0003;
            ring3.rotation.z += 0.0002;
            ring3.rotation.x -= 0.0003;

            ring1.material.color.copy(colorCurrentRef.current);
            ring2.material.color.copy(colorCurrentRef.current).multiplyScalar(0.6);
            ring3.material.color.copy(colorCurrentRef.current).multiplyScalar(0.4);

            const posArr = particleData.positions;
            const origArr = particleData.originalPositions;
            const colArr = particleData.colors;

            for (let i = 0; i < particleData.PARTICLE_COUNT; i++) {
                const i3 = i * 3;
                posArr[i3] = origArr[i3] + Math.sin(time * 2 + i * 0.1) * 0.4;
                posArr[i3 + 1] = origArr[i3 + 1] + Math.cos(time * 1.5 + i * 0.13) * 0.4;
                posArr[i3 + 2] = origArr[i3 + 2] + Math.sin(time * 1.8 + i * 0.07) * 0.3;

                const mx = mouseRef.current.x * 8;
                const my = mouseRef.current.y * 5;
                posArr[i3] += (mx - posArr[i3]) * 0.0003;
                posArr[i3 + 1] += (my - posArr[i3 + 1]) * 0.0003;

                colArr[i3] = particleColor.r;
                colArr[i3 + 1] = particleColor.g;
                colArr[i3 + 2] = particleColor.b;
            }

            particleGeometry.attributes.position.needsUpdate = true;
            particleGeometry.attributes.color.needsUpdate = true;

            const { linePositionsBuf: lpb, lineColorsBuf: lcb } = particleData;
            let lineCount = 0;
            const sampledStep = Math.max(1, Math.floor(particleData.PARTICLE_COUNT / 400));

            for (let i = 0; i < particleData.PARTICLE_COUNT && lineCount < particleData.MAX_LINES; i += sampledStep) {
                for (let j = i + sampledStep; j < particleData.PARTICLE_COUNT && lineCount < particleData.MAX_LINES; j += sampledStep) {
                    const i3 = i * 3;
                    const j3 = j * 3;
                    const dx = posArr[i3] - posArr[j3];
                    const dy = posArr[i3 + 1] - posArr[j3 + 1];
                    const dz = posArr[i3 + 2] - posArr[j3 + 2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (dist < particleData.MAX_DIST) {
                        const alpha = (1 - dist / particleData.MAX_DIST);
                        const li = lineCount * 6;
                        lpb[li] = posArr[i3];
                        lpb[li + 1] = posArr[i3 + 1];
                        lpb[li + 2] = posArr[i3 + 2];
                        lpb[li + 3] = posArr[j3];
                        lpb[li + 4] = posArr[j3 + 1];
                        lpb[li + 5] = posArr[j3 + 2];

                        lcb[li] = colArr[i3] * alpha;
                        lcb[li + 1] = colArr[i3 + 1] * alpha;
                        lcb[li + 2] = colArr[i3 + 2] * alpha;
                        lcb[li + 3] = colArr[j3] * alpha;
                        lcb[li + 4] = colArr[j3 + 1] * alpha;
                        lcb[li + 5] = colArr[j3 + 2] * alpha;

                        lineCount++;
                    }
                }
            }

            for (let i = lineCount * 6; i < particleData.MAX_LINES * 6; i++) {
                lpb[i] = 0;
                lcb[i] = 0;
            }

            particleData.lineGeometry.attributes.position.needsUpdate = true;
            particleData.lineGeometry.attributes.color.needsUpdate = true;
            particleData.lineGeometry.setDrawRange(0, lineCount * 2);
            particleData.lines.material.opacity = 0.12 + Math.sin(timeRef.current * 0.5) * 0.04;

            particleMaterial.uniforms.uTime.value = timeRef.current;
            particleMaterial.uniforms.uColor.value.copy(colorCurrentRef.current);

            renderer.render(scene, camera);
            rafRef.current = requestAnimationFrame(animate);
        };

        animate();

        // Section colors for transitions (rebuilt from the theme palette)

        const sectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const sectionIndex = parseInt(entry.target.getAttribute('data-d6-section'), 10);
                        if (!isNaN(sectionIndex) && sectionIndex < sectionColors.length) {
                            colorTargetRef.current.copy(sectionColors[sectionIndex]);
                        }
                    }
                });
            },
            { threshold: 0.3 }
        );

        setTimeout(() => {
            document.querySelectorAll('[data-d6-section]').forEach((el) => {
                sectionObserver.observe(el);
            });
        }, 500);

        const handleResize = () => {
            const nw = window.innerWidth;
            const nh = window.innerHeight;
            camera.aspect = nw / nh;
            camera.updateProjectionMatrix();
            renderer.setSize(nw, nh);
        };
        window.addEventListener('resize', handleResize);

        // Re-read the palette when the theme switches
        const offThemeChange = onThemeChange(() => {
            fx = getFxPalette();
            particleColor.set(fx.particle);
            colorTargetRef.current.set(fx.particle);
            sectionColors = buildSectionColors();
            applyThemeBlending();
        });

        const handleHoverScan = (e) => {
            const target = e.target.closest('.glass-card, .model-card, .skill-tile, .timeline-item, .about-metric, .btn, .contact-link, .input-group input, .input-group textarea');
            if (target) {
                target.classList.add('design6__scan-active');
            }
        };
        const handleHoverScanEnd = (e) => {
            const target = e.target.closest('.design6__scan-active');
            if (target) {
                target.classList.remove('design6__scan-active');
            }
        };
        document.addEventListener('mouseover', handleHoverScan, true);
        document.addEventListener('mouseout', handleHoverScanEnd, true);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('resize', handleResize);
            offThemeChange();
            document.removeEventListener('mouseover', handleHoverScan, true);
            document.removeEventListener('mouseout', handleHoverScanEnd, true);
            sectionObserver.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (rendererRef.current) {
                rendererRef.current.dispose();
                if (mount.contains(rendererRef.current.domElement)) {
                    mount.removeChild(rendererRef.current.domElement);
                }
            }
            if (cursorGlowRef.current && cursorGlowRef.current.parentNode) {
                cursorGlowRef.current.parentNode.removeChild(cursorGlowRef.current);
            }
            particleGeometry.dispose();
            particleMaterial.dispose();
            ringGeometry.dispose();
            ringMaterial.dispose();
            lineGeometry.dispose();
            lineMaterial.dispose();
        };
    }, []);

    useEffect(() => {
        const attachSectionMarkers = () => {
            const sections = document.querySelectorAll('.design6__content .section, .design6__content .hero');
            const markerIds = ['hero', 'about', 'projects', 'skills', 'career', 'contact'];
            sections.forEach((section, idx) => {
                if (idx < markerIds.length && !section.hasAttribute('data-d6-section')) {
                    section.setAttribute('data-d6-section', idx);
                }
            });
        };

        attachSectionMarkers();
        const tries = [200, 600, 1200, 2500];
        const timers = tries.map(delay => setTimeout(attachSectionMarkers, delay));

        return () => timers.forEach(t => clearTimeout(t));
    }, []);

    return (
        <div className="design6">
            <div ref={mountRef} className="design6__three-mount" />
            <div className="design6__scanlines" aria-hidden="true" />
            <div className="design6__vignette" aria-hidden="true" />
            <div className="design6__data-stream design6__data-stream--1" aria-hidden="true">
                {Array.from({ length: 12 }).map((_, i) => (
                    <span key={i} className="design6__data-bit" style={{
                        animationDelay: `${i * 0.3}s`,
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                    }} />
                ))}
            </div>
            <div className="design6__data-stream design6__data-stream--2" aria-hidden="true">
                {Array.from({ length: 8 }).map((_, i) => (
                    <span key={i} className="design6__data-bit" style={{
                        animationDelay: `${i * 0.5}s`,
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                    }} />
                ))}
            </div>
            <div className="design6__content">
                <PortfolioContent />
            </div>
        </div>
    );
}

export default Design6;